import { useCallback, useEffect, useMemo, useState } from 'react';
import { tripService, Trip, getTripImageSrc } from '../../api/trips';
import { Employee } from '../../api/Employee';
import FilterOptions from './components/FilterOptions';
import { historyTripService, HistoryTrip } from '../../api/history.trip';
import {
  MapPin,
  Search,
  Car,
  User,
  Calendar,
  Clock,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Image as ImageIcon,
  RefreshCw,
  Filter,
  History,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const statusLabelMap = {
  aprovada: 'Aprovada',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  rejeitada: 'Rejeitada',
} as const;

const statusClassMap = {
  aprovada: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  em_andamento: 'bg-blue-50 text-blue-800 ring-blue-600/20',
  concluida: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  cancelada: 'bg-slate-50 text-slate-700 ring-slate-600/20',
  rejeitada: 'bg-rose-50 text-rose-800 ring-rose-600/20',
} as const;

interface TripWithVehicle extends Trip {
  employee?: Employee;
}

const HistoricoDeViagens = () => {
  const [trips, setTrips] = useState<TripWithVehicle[]>([]);
  const [tripHistories, setTripHistories] = useState<HistoryTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedTrip, setSelectedTrip] = useState<TripWithVehicle | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const FILTER_CATEGORIES = {
    Status: [
      { id: 'aprovada', label: 'Aprovada' },
      { id: 'em_andamento', label: 'Em Andamento' },
      { id: 'concluida', label: 'Concluída' },
      { id: 'cancelada', label: 'Cancelada' },
      { id: 'rejeitada', label: 'Rejeitada' },
    ],
  };

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [allTrips, allHistories] = await Promise.all([
        tripService.getAll(),
        historyTripService.getAll(),
      ]);
      const nonPendingTrips = allTrips.filter(trip => trip.status !== 'pendente');
      setTrips(nonPendingTrips);
      setTripHistories(allHistories);
    } catch (_err) {
      setError('Erro ao buscar viagens finalizadas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getLatestTripHistory = useCallback((tripId: number) =>
    tripHistories
      .filter((history) => history.trip_id === tripId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0],
  [tripHistories]);

  const filteredTrips = useMemo(() => trips.filter((trip) => {
    const latestHistory = getLatestTripHistory(trip.id);
    const vehicleInfo = latestHistory?.vehicle_info?.toLowerCase() || '';
    const employeeInfo = latestHistory?.employee_info?.toLowerCase() || '';
    const term = searchTerm.trim().toLowerCase();

    const matchesSearch =
      !term ||
      String(trip.id).includes(term) ||
      (trip.destination || '').toLowerCase().includes(term) ||
      (trip.purpose || '').toLowerCase().includes(term) ||
      vehicleInfo.includes(term) ||
      employeeInfo.includes(term) ||
      (trip.status || '').toLowerCase().includes(term);

    const selectedStatus = appliedFilters.Status || [];
    const matchesStatus =
      selectedStatus.length === 0 || selectedStatus.includes(trip.status);

    return matchesSearch && matchesStatus;
  }), [trips, searchTerm, appliedFilters, getLatestTripHistory]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredTrips.length / ITEMS_PER_PAGE)),
    [filteredTrips.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { tripsPaginados, pageRange } = useMemo(() => {
    const total = filteredTrips.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      tripsPaginados: filteredTrips.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredTrips, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); }
    catch { return '-'; }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleString('pt-BR'); }
    catch { return '-'; }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
  };

  const handleViewTrip = (trip: TripWithVehicle) => {
    setSelectedTrip(trip);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedTrip(null);
    setIsViewModalOpen(false);
  };

  const getTripHistoriesSorted = (tripId: number) =>
    tripHistories
      .filter(h => h.trip_id === tripId)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const renderVehicleInfo = (tripId: number) => {
    const h = getLatestTripHistory(tripId);
    return h?.vehicle_info;
  };

  const renderEmployeeInfo = (tripId: number) => {
    const h = getLatestTripHistory(tripId);
    return h?.employee_info;
  };

  return (
    <>
      <PageHeader
        title="Histórico de Viagens"
        description="Viagens registradas · Histórico completo das viagens — acompanhe todas as viagens realizadas com detalhes, motoristas e check-out."
        hideTitleInBody
      />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        {_error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{_error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Todas as viagens</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Aprovadas, concluídas, rejeitadas e canceladas
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-xl">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar ID, destino, veículo, funcionário..."
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => { setSubmitting(true); void fetchData().finally(() => setSubmitting(false)); }}
                  title="Atualizar informações"
                  className="h-10 shrink-0"
                  disabled={submitting}
                >
                  <RefreshCw className={`h-4 w-4 stroke-[1.9] ${submitting ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && trips.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[14%] min-w-[170px] max-w-[220px]">Data Início</Th>
                    <Th className="w-[14%] min-w-[170px] max-w-[220px]">Data Fim</Th>
                    <Th className="w-[14%] min-w-[180px] max-w-[240px]">Veículo</Th>
                    <Th className="w-[14%] min-w-[180px] max-w-[240px]">Destino</Th>
                    <Th className="w-[14%] min-w-[200px] max-w-[280px]">Propósito</Th>
                    <Th className="w-[14%] min-w-[180px] max-w-[240px]">Funcionário</Th>
                    <Th className="w-[6%] min-w-[80px] max-w-[110px]">Checkout</Th>
                    <Th className="w-[10%] min-w-[100px] max-w-[130px]">Status</Th>
                    <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tripsPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={9}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <History className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhuma viagem encontrada</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Não há viagens finalizadas registradas no sistema.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    tripsPaginados.map((trip) => {
                      const checkoutImg = trip.images?.find(img => img.type === 'TRIP_CHECKOUT');
                      const checkoutSrc = getTripImageSrc(checkoutImg);
                      const statusLabel = statusLabelMap[trip.status as keyof typeof statusLabelMap] || trip.status;
                      const statusClasses = statusClassMap[trip.status as keyof typeof statusClassMap] || '';
                      const vehicleInfo = renderVehicleInfo(trip.id);
                      const employeeInfo = renderEmployeeInfo(trip.id);
                      return (
                        <Tr key={trip.id}>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(trip.start_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(trip.start_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(trip.end_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(trip.end_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  {vehicleInfo ? (
                                    vehicleInfo.split('\n').map((line, i) => (
                                      <div key={i} className={`min-w-0 truncate ${i === 0 ? 'text-[13.5px] font-semibold leading-tight text-slate-900' : 'text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5'}`} title={line}>
                                        {line}
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-[13.5px] font-semibold leading-tight text-slate-500">-</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                  <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={trip.destination || '-'}>
                                    {trip.destination || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[280px] overflow-hidden py-1">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                                  <FileText className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1 pt-0.5">
                                  <span className="block line-clamp-2 text-[13px] font-medium leading-snug text-slate-800" title={trip.purpose || '-'}>
                                    {trip.purpose || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  {employeeInfo ? (
                                    employeeInfo.split('\n').map((line, i) => (
                                      <div key={i} className={`min-w-0 truncate ${i === 0 ? 'text-[13.5px] font-semibold leading-tight text-slate-900' : 'text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5'}`} title={line}>
                                        {line}
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-[13.5px] font-semibold leading-tight text-slate-500">{trip.employee_id}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex justify-center">
                              {checkoutSrc ? (
                                <button
                                  onClick={() => setExpandedImage(checkoutSrc)}
                                  className="group relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ring-1 ring-inset ring-slate-200 transition-all hover:ring-blue-300 hover:shadow-md"
                                  title="Ver foto de checkout"
                                >
                                  <img
                                    src={checkoutSrc}
                                    alt="Checkout"
                                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                  />
                                </button>
                              ) : (
                                <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300">
                                  <ImageIcon className="h-5 w-5 stroke-[1.8]" />
                                </div>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClasses}`}>
                              {statusLabel}
                            </span>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewTrip(trip)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' viagem(ns)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedTrip && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                <History className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes da Viagem
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Informações completas da viagem #{selectedTrip.id}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados da viagem</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data de Início</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDateTime(selectedTrip.start_date)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data de Fim</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDateTime(selectedTrip.end_date)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Destino</span>
                    <p className="text-sm text-slate-900 font-medium">{selectedTrip.destination || '-'}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Status</span>
                    <div className="mt-0.5">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClassMap[selectedTrip.status as keyof typeof statusClassMap] || ''}`}>
                        {statusLabelMap[selectedTrip.status as keyof typeof statusLabelMap] || selectedTrip.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Propósito</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap">{selectedTrip.purpose || '-'}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo e Funcionário</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <div>
                      {(() => {
                        const vInfo = renderVehicleInfo(selectedTrip.id);
                        return vInfo ? (
                          vInfo.split('\n').map((line, i) => (
                            <div key={i} className={i === 0 ? 'text-sm text-slate-900 font-medium' : 'text-[11.5px] font-semibold text-slate-500 mt-0.5'}>
                              {line}
                            </div>
                          ))
                        ) : <p className="text-sm text-slate-500 font-medium">{selectedTrip.vehicle_id}</p>;
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Funcionário</span>
                    <div>
                      {(() => {
                        const eInfo = renderEmployeeInfo(selectedTrip.id);
                        return eInfo ? (
                          eInfo.split('\n').map((line, i) => (
                            <div key={i} className={i === 0 ? 'text-sm text-slate-900 font-medium' : 'text-[11.5px] font-semibold text-slate-500 mt-0.5'}>
                              {line}
                            </div>
                          ))
                        ) : <p className="text-sm text-slate-500 font-medium">{selectedTrip.employee_id}</p>;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedTrip.status === 'cancelada' && selectedTrip.cancellation_reason && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-rose-500 to-rose-600" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-rose-700">Motivo do Cancelamento</h3>
                </div>
                <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 sm:p-5">
                  <p className="text-sm text-rose-800 font-medium whitespace-pre-wrap">{selectedTrip.cancellation_reason}</p>
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">Imagem de Checkout</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {(() => {
                  const checkoutImgObj = selectedTrip.images?.find(img => img.type === 'TRIP_CHECKOUT');
                  const checkoutSrc = getTripImageSrc(checkoutImgObj);
                  return checkoutSrc ? (
                    <button
                      onClick={() => setExpandedImage(checkoutSrc)}
                      className="group block overflow-hidden rounded-xl border border-slate-200 transition-all hover:ring-2 hover:ring-blue-300"
                    >
                      <img
                        src={checkoutSrc}
                        alt="Checkout"
                        className="max-w-sm w-full h-auto object-cover transition-transform group-hover:scale-[1.02]"
                      />
                    </button>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-10 text-slate-400 border border-dashed border-slate-200">
                      <ImageIcon className="h-10 w-10 stroke-[1.8]" />
                      <span className="text-[12.5px] font-semibold">Nenhuma imagem de checkout disponível.</span>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-purple-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-purple-700">Histórico de Ações</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5">
                {getTripHistoriesSorted(selectedTrip.id).length === 0 ? (
                  <p className="text-[13px] font-medium text-slate-500">Nenhum histórico para esta viagem.</p>
                ) : (
                  <ul className="relative space-y-0 before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
                    {getTripHistoriesSorted(selectedTrip.id).map(h => (
                      <li key={h.id} className="relative pl-11 pb-4 last:pb-0">
                        <div className="absolute left-0 top-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm">
                          <History className="h-4 w-4 text-purple-600 stroke-[1.9]" />
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                          <div className="text-[13px] font-semibold text-slate-900">{h.action}</div>
                          {h.details && <div className="text-[12px] font-medium text-slate-500 mt-0.5 whitespace-pre-wrap">{h.details}</div>}
                          <div className="text-[11px] font-semibold text-slate-400 mt-1 tabular-nums">{new Date(h.created_at).toLocaleString('pt-BR')}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {expandedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4 sm:p-6"
          onClick={() => setExpandedImage(null)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-slate-200 transition-all hover:bg-slate-100 hover:text-slate-900 z-10"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-black/20">
              <img
                src={expandedImage}
                alt="Visualização completa"
                className="w-full max-h-[85vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(f) => { setAppliedFilters(f); setCurrentPage(1); }}
        filterCategories={FILTER_CATEGORIES}
        initialFilters={appliedFilters}
      />
    </>
  );
};

export default HistoricoDeViagens;
